/**
 * Checks whether the given ISO date string is after a specified duration from the client's current time.
 *
 * The function compares the provided ISO date (always UTC) with the client-side current time plus the specified duration.
 * JavaScript Date objects internally represent time in UTC milliseconds, so comparison is accurate
 * regardless of the user's time zone or the source of the date string.
 *
 * @param isoDateString - The ISO 8601 formatted date string (e.g., "2025-08-22T10:55:00.000Z")
 * @param minAllowDurationInMinutes - The minimum duration in minutes to allow
 * @returns Returns true if the ISO date is later than the specified duration after the client's current time, otherwise false
 *
 * @example
 * isAfterMinimumDuration("2025-08-22T10:55:00.000Z", 30); // true or false depending on local time
 */
export function isAfterMinimumDuration(
	isoDateString: string,
	minAllowDurationInMinutes: number,
): boolean {
	const now = new Date(); // Get client time (internally UTC)
	const durationFromNow = new Date(
		now.getTime() + minAllowDurationInMinutes * 60 * 1000,
	); // Add specified duration
	const targetDate = new Date(isoDateString); // Parse ISO string (UTC)
	return targetDate > durationFromNow; // Compare using UTC timestamps
}

/** Default pickup lead time when a saved/prefilled dateTime is missing or too soon. */
const DEFAULT_LEAD_TIME_MS = 3 * 60 * 60 * 1000;

/**
 * Returns the given ISO dateTime if it's present and at least
 * `minAllowDurationInMinutes` in the future; otherwise falls back to a default
 * lead time (3h) from now. Sanitizes saved/prefilled pickup times.
 */
export function coerceToValidDateTime(
	isoDateString: string | undefined,
	minAllowDurationInMinutes: number,
): string {
	if (
		isoDateString &&
		isAfterMinimumDuration(isoDateString, minAllowDurationInMinutes)
	) {
		return isoDateString;
	}
	return new Date(Date.now() + DEFAULT_LEAD_TIME_MS).toISOString();
}
